import React from 'react'
import { MaterialReactTable } from 'material-react-table'
import {
  Box,
  Paper,
  Grid,
  Typography,
  CardContent,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { unparse } from 'papaparse'
import { ziptieColors } from '../../config/themeConfig'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'

class MrtReusableTable extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
    this.tableContainerRef = React.createRef()
    this.baseState = { ...this.state }
  }

  exportFilteredDataToCsv = (table) => {
    const filteredRows = table.getFilteredRowModel().rows
    const visibleColumns = table.getVisibleLeafColumns()
    const headers = visibleColumns.map(
      (column) => column.columnDef.header || column.id,
    )
    const data = filteredRows.map((row) => {
      const rowData = {}
      visibleColumns.forEach((column) => {
        const header = column.columnDef.header || column.id
        const value = row.getValue(column.id)
        rowData[header] = value
      })
      return rowData
    })

    const csv = unparse(data, { columns: headers })
    const now = new Date()
    const formattedDate = now
      .toISOString()
      .replace(/T/, '_')
      .replace(/:/g, '-')
      .split('.')[0]
    const fileName = `${this.props.pageTitle.replace(/\s+/g, '_')}_${formattedDate}.csv`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  getColumnDescription = (keyOrAccessorKey) => {
    const { columns } = this.props
    const column = columns.find(
      (col) =>
        col.accessorKey === keyOrAccessorKey ||
        col.id === keyOrAccessorKey ||
        col.header === keyOrAccessorKey,
    )
    return column?.description
  }

  renderFieldLabel = (key, displayName, sx = {}) => {
    const description = this.getColumnDescription(key)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}>
        <Typography
          variant="subtitle1"
          color="text.primary"
          sx={{
            fontWeight: 600,
            marginBottom: '4px',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            fontSize: '0.9rem',
            flex: 1,
          }}
        >
          {displayName}
        </Typography>
        {description && (
          <Tooltip title={description} placement="top" arrow>
            <InfoOutlinedIcon
              sx={{
                fontSize: '14px',
                color: '#666',
                cursor: 'help',
                flexShrink: 0,
                '&:hover': {
                  color: '#1976d2',
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    )
  }

  render() {
    const {
      columns,
      rowData,
      defaultColumns,
      loading,
      loadingMessage,
      pageTitle,
      pageSize,
      menuButtons,
      enableRowActions,
      rowActions = () => {},
      enableExpanding = false,
      expandedRowGroups = null,
    } = this.props

    const initialColumnVisibility = columns.reduce((acc, col) => {
      if (!defaultColumns.includes(col.accessorKey)) {
        acc[col.accessorKey] = false
      }
      return acc
    }, {})

    return (
      <CardContent>
        {loading ? (
          <Grid
            container
            spacing={4}
            justifyContent={'center'}
            className="progress"
          >
            <CircularProgress />
            <Typography className="loading"> {loadingMessage}</Typography>
          </Grid>
        ) : (
          <>
            <Paper variant="outlined" sx={{ marginRight: 3 }}>
              <MaterialReactTable
                columns={columns}
                data={rowData}
                enableExpanding={false}
                getRowId={(row) => row.id}
                enablePagination={true}
                enableColumnActions={false}
                enableColumnResizing={true}
                layoutMode="semantic"
                globalFilterFn={'contains'}
                enableRowActions={enableRowActions}
                positionActionsColumn="last"
                renderRowActions={({ row }) => rowActions({ row })}
                muiPaginationProps={{
                  rowsPerPageOptions: pageSize,
                }}
                initialState={{
                  density: 'compact',
                  columnVisibility: initialColumnVisibility,
                  showColumnFilters: true,
                  pagination: {
                    pageSize: pageSize[0],
                    pageIndex: 0,
                  },
                }}
                enableDensityToggle={false}
                muiTopToolbarProps={{
                  sx: {
                    backgroundColor:
                      process.env.REACT_APP_ENV === 'stg'
                        ? ziptieColors.development.main
                        : ziptieColors.primary.main,
                    color: ziptieColors.primary.contrastText,
                    position: 'relative',
                    '& .MuiIconButton-root': {
                      color: ziptieColors.primary.contrastText,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    },
                    '& .MuiInputBase-root': {
                      color: ziptieColors.primary.contrastText,
                      '& .MuiInputBase-input': {
                        color: ziptieColors.primary.contrastText,
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          opacity: 1,
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: ziptieColors.primary.contrastText,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: ziptieColors.primary.contrastText,
                      },
                    },
                    '& .MuiFormControl-root': {
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                          color: ziptieColors.primary.contrastText,
                        },
                      },
                    },
                  },
                }}
                renderDetailPanel={
                  enableExpanding
                    ? ({ row }) => {
                        if (
                          !expandedRowGroups ||
                          typeof expandedRowGroups !== 'function'
                        ) {
                          return (
                            <Box>
                              <Grid container spacing={2}>
                                {Object.entries(row.original).map(
                                  ([key, value], index) => {
                                    const column = columns.find(
                                      (col) =>
                                        (col.accessorKey || col.id) === key,
                                    )
                                    const displayName = column?.header || key
                                    return (
                                      <Grid
                                        item
                                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                        key={index}
                                      >
                                        {this.renderFieldLabel(
                                          key,
                                          displayName,
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.8rem',
                                            color: 'text.secondary',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.4,
                                            maxHeight: '100px',
                                            padding: '4px 0',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          {String(value)}
                                        </Typography>
                                      </Grid>
                                    )
                                  },
                                )}
                              </Grid>
                            </Box>
                          )
                        }
                        const groups = expandedRowGroups(row.original)
                        if (!groups || !Array.isArray(groups)) {
                          return (
                            <Box sx={{ padding: 2 }}>
                              <Typography>No details available</Typography>
                            </Box>
                          )
                        }
                        return (
                          <Box
                            sx={{
                              margin: '0 8px 16px 8px',
                              padding: '12px 12px',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              marginLeft: '12px',
                              marginRight: '8px',
                              boxSizing: 'border-box',
                              overflowX: 'scroll',
                            }}
                          >
                            {groups.map((group, groupIndex) => (
                              <Box
                                key={groupIndex}
                                sx={{
                                  marginBottom:
                                    groupIndex < groups.length - 1 ? 3 : 0,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    marginBottom: 2,
                                    color: ziptieColors.primary.main,
                                    fontWeight: 600,
                                    borderBottom: `2px solid ${ziptieColors.primary.main}`,
                                    paddingBottom: 1,
                                    fontSize: '1.1rem',
                                  }}
                                >
                                  {group.title}
                                </Typography>
                                <Box
                                  sx={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  }}
                                >
                                  <table
                                    style={{
                                      width: '100%',
                                      borderCollapse: 'collapse',
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        style={{ backgroundColor: '#e9ecef' }}
                                      >
                                        {group.columns.map((col, colIndex) => {
                                          const description =
                                            this.getColumnDescription(
                                              col.accessorKey ||
                                                col.name ||
                                                col.header,
                                            )
                                          return (
                                            <th
                                              key={colIndex}
                                              style={{
                                                padding: '12px',
                                                textAlign: 'left',
                                                borderRight:
                                                  colIndex <
                                                  group.columns.length - 1
                                                    ? '1px solid #dee2e6'
                                                    : 'none',
                                                fontWeight: 600,
                                                color: '#495057',
                                                fontSize: '0.875rem',
                                                position: 'relative',
                                              }}
                                            >
                                              <Box
                                                component="span"
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                                }}
                                              >
                                                <span style={{ flex: 1 }}>
                                                  {col.header || col.label}
                                                </span>
                                                {description && (
                                                  <Tooltip
                                                    title={description}
                                                    placement="top"
                                                    arrow
                                                  >
                                                    <InfoOutlinedIcon
                                                      sx={{
                                                        fontSize: '14px',
                                                        color: '#666',
                                                        cursor: 'help',
                                                        flexShrink: 0,
                                                        '&:hover': {
                                                          color: '#1976d2',
                                                        },
                                                      }}
                                                    />
                                                  </Tooltip>
                                                )}
                                              </Box>
                                            </th>
                                          )
                                        })}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.data.map((dataRow, rowIndex) => (
                                        <tr
                                          key={rowIndex}
                                          style={{
                                            backgroundColor:
                                              rowIndex % 2 === 0
                                                ? '#fff'
                                                : '#f8f9fa',
                                            borderTop:
                                              rowIndex > 0
                                                ? '1px solid #dee2e6'
                                                : 'none',
                                          }}
                                        >
                                          {group.columns.map(
                                            (col, colIndex) => {
                                              const cellValue =
                                                dataRow[
                                                  col.accessorKey || col.name
                                                ]
                                              return (
                                                <td
                                                  key={colIndex}
                                                  style={{
                                                    padding: '12px',
                                                    borderRight:
                                                      colIndex <
                                                      group.columns.length - 1
                                                        ? '1px solid #dee2e6'
                                                        : 'none',
                                                    color: '#495057',
                                                    verticalAlign: 'top',
                                                    fontSize: '0.875rem',
                                                    lineHeight: '1.4',
                                                  }}
                                                >
                                                  {React.isValidElement(
                                                    cellValue,
                                                  )
                                                    ? cellValue
                                                    : cellValue || '-'}
                                                </td>
                                              )
                                            },
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )
                      }
                    : undefined
                }
                muiTableHeadCellProps={({ column }) => ({
                  sx: !['mrt-row-select', 'mrt-expand'].includes(column.id)
                    ? {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        flex: 0,
                      }
                    : {},
                })}
                muiTableBodyCellProps={({ column }) => ({
                  sx: !['mrt-row-select', 'mrt-expand'].includes(column.id)
                    ? {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        flex: 0,
                      }
                    : {},
                })}
                renderTopToolbarCustomActions={({ table }) => (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        color: ziptieColors.primary.contrastText,
                        fontWeight: 500,
                        marginRight: 'auto',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        fontSize: '1.35rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {pageTitle}
                      {this.state.isProcessing && (
                        <CircularProgress
                          size={16}
                          sx={{
                            color: ziptieColors.primary.contrastText,
                            marginLeft: 1,
                          }}
                        />
                      )}
                    </Typography>
                    {menuButtons}
                    <Tooltip title="Clear All Filters">
                      <IconButton
                        onClick={() => {
                          requestAnimationFrame(() => {
                            table.resetColumnFilters()
                            table.setGlobalFilter('')
                          })
                        }}
                        disabled={
                          !table.getState().columnFilters.length &&
                          !table.getState().globalFilter
                        }
                        sx={{
                          color:
                            !table.getState().columnFilters.length &&
                            !table.getState().globalFilter
                              ? 'rgba(255, 255, 255, 0.3)'
                              : ziptieColors.primary.contrastText,
                          marginRight: '-4px',
                          '&:disabled': {
                            color: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover:not(:disabled)': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <FilterAltOffIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Filtered Data to CSV">
                      <IconButton
                        onClick={() => this.exportFilteredDataToCsv(table)}
                        sx={{
                          color: ziptieColors.primary.contrastText,
                          marginLeft: '-4px',
                        }}
                      >
                        <FileDownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              />
            </Paper>
          </>
        )}
      </CardContent>
    )
  }
}

export default MrtReusableTable
